import { useSyncExternalStore } from 'react';

/**
 * Fuente de verdad única de la clase en vivo.
 *
 * Escriben useVideoSession.js (SDK) y useLiveClassSignals.js (command channel);
 * el escenario de video, el menú flotante y el tab «En vivo» leen el mismo
 * estado para no divergir entre sí.
 */
const initialState = {
    /** idle | connecting | connected | reconnecting | closed | failed */
    connection: 'idle',
    reconnectAttempt: 0,
    selfUserId: null,
    isHost: false,
    microphoneEnabled: false,
    cameraEnabled: false,
    screenShareEnabled: false,
    shareLocked: false,
    /** El profesor pausó su pantalla compartida: los alumnos ven el último fotograma. */
    screenFrozen: false,
    /** idle | recording | paused */
    recordingState: 'idle',
    recordingBusy: false,
    participants: [],
    activeSpeakerId: null,
    activeShareUserId: null,
    /** userIds hablando ahora (con decaimiento para evitar parpadeo). */
    speakingUserIds: [],
    raisedHandUserIds: [],
    /** userId -> { uplink, downlink } (0-5, mayor es mejor) */
    networkQuality: {},
};

let state = initialState;
const listeners = new Set();

const emit = () => {
    listeners.forEach((listener) => {
        try {
            listener();
        } catch (err) {
            console.warn('[LiveClass] listener:', err?.message || err);
        }
    });
};

const shallowEqual = (a, b) => {
    const keys = Object.keys(b);
    return keys.every((key) => a[key] === b[key]);
};

export const getLiveClassState = () => state;

export const subscribeLiveClass = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};

/** Aplica un patch; no notifica si ninguna referencia cambió. */
export const patchLiveClassState = (patch) => {
    if (!patch || shallowEqual(state, patch)) return;
    state = { ...state, ...patch };
    emit();
};

export const resetLiveClassState = () => {
    state = initialState;
    emit();
};

const identity = (value) => value;

/**
 * Suscribe un componente al store. El selector debe devolver un valor estable
 * (primitivo o la misma referencia) para evitar renders innecesarios.
 */
export const useLiveClassState = (selector = identity) => (
    useSyncExternalStore(subscribeLiveClass, () => selector(state))
);

export const RECORDING_STATE = {
    IDLE: 'idle',
    RECORDING: 'recording',
    PAUSED: 'paused',
};

export const CONNECTION_STATE = {
    IDLE: 'idle',
    CONNECTING: 'connecting',
    CONNECTED: 'connected',
    RECONNECTING: 'reconnecting',
    CLOSED: 'closed',
    FAILED: 'failed',
};

/** Traduce el RecordingStatus del SDK al estado que consume la UI. */
export const mapRecordingStatus = (status) => {
    if (status === 'Recording') return RECORDING_STATE.RECORDING;
    if (status === 'Paused') return RECORDING_STATE.PAUSED;
    return RECORDING_STATE.IDLE;
};
